import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

// IMPORT CSS
import "../../theme/style.scss";

// IMPORT UI COMPONENTS
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Popover,
  Pagination,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import CustomBreadcrumbs from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { AlertType } from "../../services/models/AlertType";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";

// IMPORT SERVICE
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { PAGE_SIZE } from "../../services/common/const";
import { PreferencesContext } from "../../PreferenceContext";
import {
  GetProcedureList,
  DeleteProcedure,
} from "../../services/ProcedureService";

// SCHEMA
interface Data {
  procedureId: string;
  name: string;
  typeCode: string;
  price: number;
  durationValue: number;
  durationUnitCode: string;
  durationUnitName: string;
  none: string;
}

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  consumerId: string;
  organizationId: string;
  branchId: string;
  departmentId: string;
  specialityId: string;
  procedureId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  procedureType: string;
  mode: string;
}

// DEFAULT PARAMS
const parameters: Parameter = {
  procedureId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "name",
  sortDirection: "ASC",
  searchValue: "",
  procedureType: "SINGLE_VISIT",
  mode: "",
  consumerId: "",
  organizationId: "",
  branchId: "",
  departmentId: "",
  specialityId: "",
};

// SCHEMA FOR RESULTDATA
interface ResultData {
  procedureColl: Array<any>;
  totalCount: number;
  tabTypeColl?: Array<{
    tabType: string;
    total: number;
  }>;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  procedureColl: [],
  totalCount: 0,
  tabTypeColl: [],
};

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

const PROCEDURE_TABS = [
  { value: "all-visits", code: "" },
  { value: "single-visit", code: "SINGLE_VISIT" },
  { value: "multiple-visit", code: "MULTIPLE_VISIT" },
  { value: "sub-visit", code: "SUB_VISIT" },
];
const getTabCode = (typeCode?: string) => {
  if (typeCode === "MULTIPLE_VISIT") return "multiple-visit";
  if (typeCode === "SUB_VISIT") return "sub-visit";
  return "single-visit"; // default
};

const getProcedureTypeCode = (tabValue: string) => {
  if (tabValue === "all-visits") return "";
  if (tabValue === "multiple-visit") return "MULTIPLE_VISIT";
  if (tabValue === "sub-visit") return "SUB_VISIT";
  return "SINGLE_VISIT";
};

const getTabTotal = (data: ResultData, tabValue: string) => {
  if (tabValue === "all-visits") {
    return data?.tabTypeColl?.find((x) => x.tabType === "ALL")?.total || 0;
    // return (
    //   (data?.tabTypeColl || []).reduce(
    //     (sum: number, item: any) => sum + Number(item?.total || 0),
    //     0,
    //   ) ||
    //   data?.totalCount ||
    //   0
    // );
  }
  const tabType = getProcedureTypeCode(tabValue);
  return data?.tabTypeColl?.find((x) => x?.tabType === tabType)?.total || 0;
};

export default function ProcedureList() {
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }
  const queryParams = useQuery();
  const tabParam = queryParams.get("tab");
  const pageParam = queryParams.get("page");
  const searchParam = queryParams.get("q");

  const curTabValue =
    tabParam === "all-visits" ||
    tabParam === "single-visit" ||
    tabParam === "multiple-visit" ||
    tabParam === "sub-visit"
      ? tabParam
      : "single-visit";
  const curPage = pageParam ? +pageParam : 1;

  const [order, setOrder] = React.useState<Order>("");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const [page, setPage] = React.useState(0);
  const [value, setValue] = useState(searchParam || "");
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);

  // STATE FOR STORAGE
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [procedureId, setProcedureId] = useState(0);
  const [tabType, setTabType] = useState<string>("");
  const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
    null,
  );

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  // HANDLE POPUP FOR EDIT AND DELETE
  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    procedureId: any,
    selectedTabType?: string,
  ) => {
    setAnchorEl3(event.currentTarget);
    setProcedureId(procedureId);
    setTabType(String(selectedTabType ?? ""));
  };

  // CLOSE POPUP
  const handleCloseMore = () => {
    setAnchorEl3(null);
  };

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
            }}
            align="left"
          >
            Procedure Name
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "none",
                md: "table-cell",
              },
              fontWeight: "bold",
            }}
            align="left"
          >
            Type
          </TableCell>

          <TableCell
            sx={{
              display: {
                xs: "none",
                md: "table-cell",
              },
              fontWeight: "bold",
            }}
            align="left"
          >
            Duration
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "none",
                md: "table-cell",
              },
              fontWeight: "bold",
            }}
            align="right"
          >
            Price
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "table-cell",
                sm: "table-cell",
                md: "table-cell",
                lg: "table-cell",
              },
              fontWeight: "bold",
              width: "10%",
            }}
            align="left"
          ></TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number,
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number],
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // GET LIST OF PROCEDURES
  const getProcedureList = async () => {
    setIsLoading(true);
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentId", userContext.departmentId?.toString());
      params.append("specialityId", userContext.specialityId?.toString());
      params.append("pageIndex", curPage.toString());
      params.append("pageSize", PAGE_SIZE.toString());
      params.append("sortColumn", parameters.sortColumn);
      params.append("sortDirection", parameters.sortDirection);
      params.append("searchValue", searchParam || "");
      params.append("procedureTypeCode", getProcedureTypeCode(curTabValue));
      params.append("mode", "");

      const result: any = await GetProcedureList(params);
      if (result?.data?.data?.procedureColl) {
        const data: ResultData = result?.data?.data;
        let procedureColl = data?.procedureColl || [];
        let dataCount = data?.totalCount || 0;
        setCounData(Math.ceil(dataCount / PAGE_SIZE));
        setResultData(data);
        setPageStartIndex((curPage - 1) * PAGE_SIZE + 1);
        setPageEndIndex((curPage - 1) * PAGE_SIZE + procedureColl.length);
        setTotalCount(dataCount);
      }
    } catch (e) {
      console.log("err:", e);
    } finally {
      setIsLoading(false);
    }
  };

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    navigate(
      `?tab=${curTabValue}&page=${value}&q=${encodeURIComponent(searchParam || "")}`,
    );
  };

  // ON SEARCH
  function searchValue(val: any) {
    if (val === "") setValue("");
    navigate(
      `?tab=${curTabValue}&page=1&q=${encodeURIComponent(val.toString())}`,
    );
  }

  // FORMAT PRICE
  const formatPrice = (price: number) => {
    if (!price) return "₹0";
    return `₹${price.toLocaleString("en-IN")}`;
  };

  const formatProcedureName = (name?: string) => {
    if (!name) return "";
    return name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatDuration = (hours?: number | null, minutes?: number | null) => {
    const totalMinutes = Number(hours || 0) * 60 + Number(minutes || 0);

    if (!totalMinutes) return "-";

    const formattedHours = Math.floor(totalMinutes / 60);
    const formattedMinutes = totalMinutes % 60;
    const parts = [];

    if (formattedHours > 0) {
      parts.push(`${formattedHours} hr${formattedHours > 1 ? "s" : ""}`);
    }

    if (formattedMinutes > 0) {
      parts.push(`${formattedMinutes} min${formattedMinutes > 1 ? "s" : ""}`);
    }

    return parts.join(" ");
  };

  // HANDLE UPDATE
  const handleUpdate = (procedureId: any, tab: string) => {
    let encryptProcedureId: any = edService.encrypt(procedureId);
    navigate(
      {
        pathname: "procedure-save",
        search: `?q=${encodeURIComponent(encryptProcedureId)}&t=${tab}`,
      },
      {
        state: {
          title: procedureId === 0 ? "Add" : "Edit",
          button: procedureId === 0 ? "Save" : "Update",
          procedureId: procedureId,
        },
      },
    );
  };

  // HANDLE DELETE WARNING POPUP
  const handleDeleteDilog = (value: any) => {
    setOpenDeleteDilog(value);
  };

  // HANDLE DELETE
  async function deleteRow(procedureId: any) {
    try {
      setAnchorEl3(null);
      handleDeleteDilog(false);
      let DeleteData = {
        procedureIds: procedureId?.toString(),
        specialityId: userContext?.specialityId.toString(),
        departmentId: userContext?.departmentId.toString(),
        branchId: userContext?.branchId?.toString(),
        organizationId: userContext?.organizationId?.toString(),
        consumerId: userContext?.consumerId.toString(),
        isDeleted: true,
        rowManagedBy: userContext?.loginDetail?.userId,
      };
      const result: any = await DeleteProcedure(DeleteData);
      if (result) {
        await getProcedureList();
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.procedureColl?.map(
        (n: any) => n?.procedureId || 0,
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // USEEFFECTS
  useEffect(() => {
    getProcedureList();
  }, [curTabValue, curPage, searchParam]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50);
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <>
      <Box>
        <Box className="main-container">
          <Grid container className="page-header">
            <Grid item xs={6}>
              <Box
                sx={{ display: "flex", height: "100%", alignItems: "center" }}
              >
                <CustomBreadcrumbs />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleUpdate(
                      0,
                      curTabValue === "all-visits"
                        ? "single-visit"
                        : curTabValue,
                    );
                  }}
                  size="medium"
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container className="list-container">
            <Grid item xs={8} sm={8} md={8} lg={10} sx={{ gap: 2 }}>
              <SkeletonWrapper
                isLoading={showSkeleton}
                className="skeleton-search-box"
              >
                <TextField
                  id="searchBox"
                  placeholder="Search..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      searchValue(value);
                    }
                  }}
                  type="text"
                  variant="outlined"
                  size="small"
                  onChange={(e) => setValue(e.target.value)}
                  value={value}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          onClick={() => {
                            searchValue(value);
                          }}
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: value && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => {
                          searchValue("");
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                />
              </SkeletonWrapper>
            </Grid>
            <Grid item xs={12}>
              <Tabs
                value={curTabValue}
                onChange={(_: React.SyntheticEvent, newValue: string) => {
                  navigate(
                    `?tab=${newValue}&page=1&q=${encodeURIComponent(searchParam || "")}`,
                  );
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  // borderBottom: "1px solid #e0e0e0",
                  // mb: 1,
                  "& .MuiTab-root": {
                    textTransform: "none !important",
                  },
                }}
              >
                <Tab
                  value="single-visit"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span>Single Visit</span>
                      <Box
                        className="inner-tabs"
                        sx={{
                          minWidth: 22,
                          height: 22,
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 0.75,
                          fontSize: "12px",
                          fontWeight: 600,
                          bgcolor: "#FFF4CC",
                          color: "#333333",
                        }}
                      >
                        {getTabTotal(resultData, "single-visit")}
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="multiple-visit"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span>Multiple Visit</span>
                      <Box
                        className="inner-tabs"
                        sx={{
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 0.75,
                          fontSize: "12px",
                          fontWeight: 600,
                          bgcolor: "#E3F2FD",
                          color: "#333333",
                        }}
                      >
                        {getTabTotal(resultData, "multiple-visit")}
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="sub-visit"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span>Sub Visit</span>
                      <Box
                        className="inner-tabs"
                        sx={{
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 0.75,
                          fontSize: "12px",
                          fontWeight: 600,
                          bgcolor: "#e1e1e1",
                          color: "#333333",
                        }}
                      >
                        {getTabTotal(resultData, "sub-visit")}
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="all-visits"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <span>All Visits</span>
                      <Box
                        className="inner-tabs"
                        sx={{
                          minWidth: 22,
                          height: 22,
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 0.75,
                          fontSize: "12px",
                          fontWeight: 600,
                          bgcolor: "#E8F5E9",
                          color: "#333333",
                        }}
                      >
                        {getTabTotal(resultData, "all-visits")}
                      </Box>
                    </Box>
                  }
                />
              </Tabs>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <TableContainer>
                <Table>
                  <EnhancedTableHead
                    numSelected={resultData?.procedureColl?.length || 0}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleCheckSelectAll}
                    rowCount={resultData?.procedureColl?.length}
                  />
                  <TableBody>
                    {showSkeleton ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={5}>
                                <SkeletonWrapper
                                  isLoading={showSkeleton}
                                  className="skeleton-input"
                                >
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.procedureColl,
                          getComparator(order, orderBy),
                        )
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          .map((item: any, index: any) => {
                            return (
                              <TableRow key={item?.procedureId?.toString()}>
                                <TableCell>
                                  <Link
                                    className="text-link"
                                    to={{
                                      pathname: "procedure-save",
                                      search: `?q=${encodeURIComponent(
                                        edService.encrypt(
                                          item?.procedureId || 0,
                                        ) || 0,
                                      )}&t=${getTabCode(item?.typeCode)}`,
                                    }}
                                  >
                                    {item?.name}
                                  </Link>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      md: "table-cell",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "inline-block",
                                      px: 1.5,
                                      py: 0.3,
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      backgroundColor:
                                        item?.typeCode === "SINGLE_VISIT"
                                          ? "#FFF4CC"
                                          : item?.typeCode === "SUB_VISIT"
                                          ? "#e1e1e1"
                                          : "#E3F2FD",
                                      color: "#333333 !important",
                                    }}
                                  >
                                    {item?.typeCode === "SINGLE_VISIT"
                                      ? "Single"
                                      : item?.typeCode === "SUB_VISIT"
                                      ? "Sub"
                                      : "Multiple"}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      md: "table-cell",
                                    },
                                  }}
                                >
                                  {formatDuration(
                                    item.durationHoursValue,
                                    item.durationMinuteValue,
                                  )}
                                </TableCell>

                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      md: "table-cell",
                                    },
                                  }}
                                  align="right"
                                >
                                  {formatPrice(item.price)}
                                </TableCell>

                                {/* <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      md: "table-cell",
                                    },
                                    backgroundColor:
                                      item?.typeCode === "SINGLE_VISIT"
                                        ? "#c19603"
                                        : "black",
                                    color: "#fff !important",
                                  }}
                                >
                                  {item?.typeCode === "SINGLE_VISIT"
                                    ? "Single"
                                    : "Multiple"}
                                </TableCell> */}
                                <TableCell align="right">
                                  <Tooltip title="More" placement="top">
                                    <IconButton
                                      onClick={(e: any) => {
                                        const tabType =
                                          PROCEDURE_TABS.find(
                                            (x) => x.code === item?.typeCode,
                                          )?.value || "";
                                        handleOpenMore(
                                          e,
                                          item?.procedureId || 0,
                                          tabType,
                                        );
                                      }}
                                    >
                                      <MoreVertRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {resultData?.procedureColl?.length <= 0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={5}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} paddingTop={1}>
              {resultData?.procedureColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-pagination-label"
                    >
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-pagination-stack"
                    >
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData}
                          page={curPage}
                          onChange={handleChangePageNew}
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Popover
        elevation={1}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMore}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                handleUpdate(procedureId, tabType);
              }}
            >
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              className="delete-container"
              onClick={() => {
                handleDeleteDilog(true);
              }}
            >
              Delete
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>

      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          handleDeleteDilog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this Procedure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              deleteRow(procedureId);
            }}
            className="button-delete"
          >
            Yes
          </Button>
          <Button
            onClick={(e) => {
              handleDeleteDilog(false);
            }}
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
